import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { SquareImageRemoveButton } from './square-image-remove-button.atom'
import type { ImageSize } from './square-image-upload.helper'
import { SquareImageUploadButton } from './square-image-upload-button.atom'
import { useSquareImageUpload } from './use-square-image-upload.hook'

export interface SquareImageUploadProps {
  imageUrl: string | null
  fallbackText: string
  onImageChange: (file: File | null) => void
  onImageRemove: () => void
  isDisabled?: boolean
  size?: ImageSize
  uploadTooltip?: string
  changeTooltip?: string
  removeTooltip?: string
  className?: string
}

export const SquareImageUpload = ({
  imageUrl,
  fallbackText,
  onImageChange,
  onImageRemove,
  isDisabled = false,
  size = 'lg',
  uploadTooltip = 'Upload image',
  changeTooltip = 'Change image',
  removeTooltip = 'Remove image',
  className,
}: SquareImageUploadProps): ReactNode => {
  const { fileInputRef, handleClick, handleFileChange, handleRemove } = useSquareImageUpload({
    isDisabled,
    onImageChange,
    onImageRemove,
  })

  return (
    <div className={cn('square-image-upload group relative', className)}>
      <SquareImageUploadButton
        imageUrl={imageUrl}
        fallbackText={fallbackText}
        isDisabled={isDisabled}
        size={size}
        uploadTooltip={uploadTooltip}
        changeTooltip={changeTooltip}
        onClick={handleClick}
      />
      {imageUrl && !isDisabled && (
        <SquareImageRemoveButton
          size={size}
          removeTooltip={removeTooltip}
          onRemove={handleRemove}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        aria-label="Upload image"
      />
    </div>
  )
}

SquareImageUpload.displayName = 'SquareImageUpload'
